import Box from '@mui/material/Box';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import type { PickersActionBarAction } from '@mui/x-date-pickers/PickersActionBar';
import type { Dayjs } from 'date';
import { type ReactNode, useEffect, useState } from 'react';

import { useBreakpoint } from '../../hooks/useBreakpoint/useBreakpoint';
import type { SelectDatePickerProps } from '../SelectDatePicker/SelectDatePicker';
import { Typography } from '../Typography/Typography';

const DEFAULT_FROM_LABEL = 'From';
const DEFAULT_TO_LABEL = 'To';
const DEFAULT_SEPARATOR = 'to';
const ACTION_BAR_ACTIONS: PickersActionBarAction[] = ['today'];

export type DateRange = {
  from: Dayjs;
  to: Dayjs;
};

export interface DateRangeProps {
  onChange: SelectDatePickerProps['onChange'];
  onAccept?: () => void;
  defaultValues: DateRange;
  slotProps?: {
    from?: {
      label?: string;
      size?: 'small' | 'medium';
    };
    separator?: ReactNode;
    to?: {
      label?: string;
      size?: 'small' | 'medium';
    };
  };
}

export const DateRangePicker = ({ onChange, onAccept, defaultValues, slotProps }: DateRangeProps) => {
  const [from, setFrom] = useState<Dayjs>(defaultValues.from);
  const [to, setTo] = useState<Dayjs>(defaultValues.to);
  const [openPicker, setOpenPicker] = useState<'from' | 'to' | null>(null);

  const { isMobile } = useBreakpoint();

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setFrom(defaultValues.from);
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setTo(defaultValues.to);
  }, [defaultValues.from, defaultValues.to]);

  const handleRangeChange = ({ from: nextFrom, to: nextTo }: DateRange) => {
    setFrom(nextFrom);
    setTo(nextTo);
    onChange({
      from: nextFrom,
      to: nextTo,
    });
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        gap: 2,
        alignItems: 'center',
        maxWidth: 400,
      }}
    >
      <DesktopDatePicker
        closeOnSelect
        format="DD/MM/YYYY"
        label={slotProps?.from?.label ?? DEFAULT_FROM_LABEL}
        value={from}
        data-testid="from-date-picker"
        open={openPicker === 'from'}
        onOpen={() => {
          setOpenPicker('from');
        }}
        onClose={() => {
          setOpenPicker((currentOpenPicker) => (currentOpenPicker === 'from' ? null : currentOpenPicker));
        }}
        slotProps={{
          actionBar: {
            actions: ACTION_BAR_ACTIONS,
          },
          day: {
            onClick: (event) => {
              event.stopPropagation();
            },
          },
          openPickerButton: {
            // @ts-expect-error - data-testid is a valid prop, despite not being in the type
            'data-testid': 'from-picker-button',
          },
          textField: {
            fullWidth: true,
            id: 'date-picker',
            onClick: (event) => {
              event.stopPropagation();
            },
            size: slotProps?.from?.size ?? 'small',
          },
        }}
        onChange={(date) => {
          if (!date) {
            return;
          }

          const nextTo = date.isAfter(to, 'day') ? date : to;

          handleRangeChange({
            from: date,
            to: nextTo,
          });

          setOpenPicker('to');
        }}
      />
      {isMobile ? null : <Typography>{slotProps?.separator ?? DEFAULT_SEPARATOR}</Typography>}
      <DesktopDatePicker
        closeOnSelect
        format="DD/MM/YYYY"
        data-testid="to-date-picker"
        label={slotProps?.to?.label ?? DEFAULT_TO_LABEL}
        minDate={from}
        value={to}
        open={openPicker === 'to'}
        onAccept={() => {
          setOpenPicker(null);
          onAccept?.();
        }}
        onOpen={() => {
          setOpenPicker('to');
        }}
        onClose={() => {
          setOpenPicker((currentOpenPicker) => (currentOpenPicker === 'to' ? null : currentOpenPicker));
        }}
        slotProps={{
          actionBar: {
            actions: ACTION_BAR_ACTIONS,
          },
          day: {
            onClick: (event) => {
              event.stopPropagation();
            },
          },
          openPickerButton: {
            // @ts-expect-error - data-testid is a valid prop, despite not being in the type
            'data-testid': 'to-picker-button',
          },
          textField: {
            fullWidth: true,
            onClick: (event) => {
              event.stopPropagation();
            },
            size: slotProps?.to?.size ?? 'small',
          },
        }}
        onChange={(date) => {
          if (!date) {
            return;
          }

          const nextTo = date.isBefore(from, 'day') ? from : date;

          handleRangeChange({
            from,
            to: nextTo,
          });

          setOpenPicker(null);
        }}
      />
    </Box>
  );
};
